import Decimal from 'decimal.js';

import type { EventVo } from 'models/event';
import {
    marketExpectedExpirationTime,
    marketLastPriceCents,
    marketLatestExpirationTime,
    marketPrevPeriodPriceCents,
    marketPreviousDayPriceCents,
    marketVolume24hContracts,
    marketVolumeContracts,
    marketYesAskCents,
    marketYesBidCents,
    type MarketVo,
    subtractCents,
    sumContracts,
} from 'models/market';
import { SearchSeriesSortCriteria } from 'models/search';
import { highestVolumeMarketTicker } from 'utils/concentrationLevel';
import type { ForecastAccuracySubject } from 'utils/forecastAccuracy';

import { yesProbabilityFromCents } from './yesProbability';

/**
 * Pure race-market pricing math, shared by the server snapshot
 * (./raceMarkets.ts fetches into these shapes) and client-side re-pricing
 * (the historical timeline adapts its snapshot into the same shapes) — keep
 * this module free of server plumbing so it stays client-safe.
 *
 * Political party comes from the BFF card's hydrated structured target when
 * available (attached to `RaceMarket.party` by ./midtermsHubData.ts), with the
 * ticker suffix retained as the fallback. Events may also carry independent
 * markets, which are ignored by the major-party pricing math.
 */
const DEM_MARKET_SUFFIXES = new Set(['D', 'DEM']);
const REP_MARKET_SUFFIXES = new Set(['R', 'GOP']);
const IND_MARKET_SUFFIXES = new Set(['I', 'IND']);

export type RaceMarketParty = 'dem' | 'rep' | 'ind';
export type MajorRaceParty = Exclude<RaceMarketParty, 'ind'>;
export type RaceMarketParties = Readonly<Record<string, RaceMarketParty>>;

/**
 * The race fields the hub reads. Prices are numeric cents (0-100): they are derived
 * from the v1 events' full-precision `*_dollars` strings at the fetch boundary
 * (`trimRaceEvents`), and the snapshot/history decoders build them directly.
 * Volumes are one-sided contract counts (one side of each trade), parsed from the
 * v1 `*_contracts_fp` strings at the same boundary. They are optional because the
 * historical timeline snapshot (./midtermsSnapshot.ts) adapts a price-only payload
 * into this shape.
 */
export type RaceMarket = Pick<MarketVo, 'ticker_name'> & {
    yes_bid: number;
    yes_ask: number;
    last_price: number;
    previous_day_price?: number;
    prev_period_price?: number;
    /** Lifetime volume in contracts, one side of each trade. */
    volume_contracts?: number;
    /** Trailing-24h volume in contracts, one side of each trade. */
    volume_24h_contracts?: number;
} & Partial<
        Pick<
            MarketVo,
            | 'result'
            | 'open_date'
            | 'close_date'
            | 'latest_expiration_time'
            | 'expected_expiration_time'
            // Legacy names: history snapshots persisted before the rename still carry them.
            | 'expiration_date'
            | 'expected_expiration_date'
        >
    > & {
        /** Hydrated political-party identity; authoritative over the ticker suffix. */
        party?: RaceMarketParty;
    };

/** The v1 events market fields `trimRaceEvents` reads. */
export type RaceSourceMarket = Pick<
    MarketVo,
    'ticker_name' | 'yes_bid_dollars' | 'yes_ask_dollars' | 'last_price_dollars'
> &
    Partial<
        Pick<
            MarketVo,
            | 'result'
            | 'volume_contracts_fp'
            | 'volume_24h_contracts_fp'
            | 'dollar_volume'
            | 'dollar_recent_volume'
            | 'previous_day_price_dollars'
            | 'prev_period_price_dollars'
            | 'open_date'
            | 'close_date'
            | 'latest_expiration_time'
            | 'expected_expiration_time'
            | 'expiration_date'
            | 'expected_expiration_date'
        >
    > & {
        party?: RaceMarketParty;
    };

export type RaceEvent = Pick<EventVo, 'ticker'> & {
    markets?: RaceMarket[];
    /** Stable map region id carried by immutable history artifacts. */
    region?: string;
};

function trimRaceMarket(market: RaceSourceMarket): RaceMarket {
    return {
        ticker_name: market.ticker_name,
        yes_bid: marketYesBidCents(market),
        yes_ask: marketYesAskCents(market),
        last_price: marketLastPriceCents(market),
        result: market.result,
        volume_contracts: marketVolumeContracts(market),
        volume_24h_contracts: marketVolume24hContracts(market),
        previous_day_price: marketPreviousDayPriceCents(market),
        prev_period_price: marketPrevPeriodPriceCents(market),
        open_date: market.open_date,
        close_date: market.close_date,
        latest_expiration_time: marketLatestExpirationTime(market),
        expected_expiration_time: marketExpectedExpirationTime(market),
        party: market.party,
    };
}

/**
 * Drops every field outside RaceEvent/RaceMarket (the TS cast at the fetch
 * boundary strips nothing at runtime). The hub snapshots persist their events
 * to the deployment-wide remote cache, whose per-entry size limit the raw
 * House payload (436 events with rules text, titles, settlement details)
 * overflows — the cache write then 413s and the snapshot is never stored.
 * Keep the copied fields in sync with the types above. `region` is
 * intentionally not copied: it only exists on artifact-decoded events, which
 * never pass through this trim.
 */
export function trimRaceEvents(
    events: readonly (Pick<EventVo, 'ticker'> & { markets?: RaceSourceMarket[] })[],
): RaceEvent[] {
    return events.map((event) => ({ ticker: event.ticker, markets: event.markets?.map(trimRaceMarket) }));
}

/**
 * True once a race's outcome is public. `result` is the only terminal signal the
 * trimmed race payload carries, and the BFF publishes it for every market in an
 * event at once, so "all markets have a result" is the event-level test.
 *
 * A determined race is no longer forecast, so it stops showing a forecast
 * accuracy score — the number would be describing a settled question.
 */
export function raceEventIsDetermined(event: RaceEvent): boolean {
    const markets = event.markets ?? [];
    return markets.length > 0 && markets.every((market) => !!market.result);
}

/** One event's summed lifetime volume across its markets, in one-sided contracts. */
function sumMarketVolume(event: RaceEvent): number {
    return sumContracts((event.markets ?? []).map((market) => market.volume_contracts ?? 0));
}

/**
 * One event's lifetime volume in dollars: its one-sided contract volume, each
 * contract paying out $1 — the figure the BFF card reports as `total_event_volume`.
 */
export function eventDollarVolume(event: RaceEvent): number {
    return sumMarketVolume(event);
}

/**
 * The named event's lifetime dollar volume, or null when the payload carries no
 * such event. Null rather than 0 on purpose: an absent event means the volume is
 * unknown, and the accuracy lookup scores 0 as a real (thin) volume.
 */
export function eventDollarVolumeByTicker(events: readonly RaceEvent[], eventTicker: string): number | null {
    const event = events.find((candidate) => candidate.ticker === eventTicker);
    return event === undefined ? null : eventDollarVolume(event);
}

/**
 * What a forecast card's accuracy pill describes, from one lookup of the named
 * event. The volume is null when there is no score to show: the event is missing
 * from the payload (volume unknown), or its outcome is already determined
 * (accuracy would be describing a settled question) — the rule the event page
 * applies via `eventIsSettled`. The concentration market is the event's
 * highest-volume one.
 */
export function forecastAccuracySubjectByTicker(
    events: readonly RaceEvent[],
    eventTicker: string,
): ForecastAccuracySubject {
    const event = events.find((candidate) => candidate.ticker === eventTicker);
    if (event === undefined) return { eventTicker, volume: null, concentrationMarketTicker: null };
    return {
        eventTicker,
        volume: raceEventIsDetermined(event) ? null : eventDollarVolume(event),
        concentrationMarketTicker: highestVolumeMarketTicker(
            (event.markets ?? []).map((market) => ({ ticker: market.ticker_name, volume: market.volume_contracts })),
        ),
    };
}

/** One event's summed lifetime volume, from `rankEventsByVolume`. */
export interface EventVolume {
    ticker: string;
    volume: number;
}

export interface EventSortScore {
    ticker: string;
    score: number;
}

/**
 * Events by total traded volume, descending: each event's markets' lifetime
 * volume summed (party, candidate and independent markets alike — "most
 * traded race" means the whole event). Never-traded events are dropped — a
 * zero-volume race isn't "most traded" even when fewer than the cap have
 * traded. Ties keep the input order (sort is stable).
 */
export function rankEventsByVolume(events: readonly RaceEvent[]): EventVolume[] {
    return events
        .map((event) => ({ ticker: event.ticker, volume: sumMarketVolume(event) }))
        .filter((entry) => entry.volume > 0)
        .sort((a, b) => b.volume - a.volume);
}

const validTimestamp = (value: string | undefined): number | null => {
    if (value === undefined) return null;
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? null : timestamp;
};

const maxOrNull = (values: Array<number | null>): number | null => {
    const usable = values.filter((value): value is number => value !== null);
    return usable.length === 0 ? null : Math.max(...usable);
};

const minOrNull = (values: Array<number | null>): number | null => {
    const usable = values.filter((value): value is number => value !== null);
    return usable.length === 0 ? null : Math.min(...usable);
};

/**
 * A category-style score for one race event. Higher scores always render
 * first; date/50-50 sorts invert their natural metric to preserve that rule.
 */
function eventSortScore(event: RaceEvent, sort: SearchSeriesSortCriteria): number | null {
    const markets = event.markets ?? [];

    switch (sort) {
        case SearchSeriesSortCriteria.VOLUME:
            return sumMarketVolume(event);
        case SearchSeriesSortCriteria.TRENDING:
            return sumContracts(markets.map((market) => market.volume_24h_contracts ?? 0));
        case SearchSeriesSortCriteria.PRICE_DELTA:
            return maxOrNull(
                markets.map((market) => {
                    const previous = market.previous_day_price ?? market.prev_period_price;
                    return previous === undefined ? null : Math.abs(subtractCents(market.last_price, previous));
                }),
            );
        case SearchSeriesSortCriteria.NEWEST:
            return maxOrNull(markets.map((market) => validTimestamp(market.open_date)));
        case SearchSeriesSortCriteria.CLOSING: {
            const nextClose = minOrNull(
                markets.flatMap((market) => [
                    validTimestamp(market.close_date),
                    validTimestamp(marketLatestExpirationTime(market)),
                    validTimestamp(marketExpectedExpirationTime(market)),
                ]),
            );
            return nextClose === null ? null : -nextClose;
        }
        case SearchSeriesSortCriteria.PRICE_BALANCE: {
            const closest = minOrNull(
                markets.map((market) => {
                    const probability = marketYesProbability(market);
                    return probability === null ? null : new Decimal(probability).minus(0.5).abs().toNumber();
                }),
            );
            return closest === null ? null : -closest;
        }
        default:
            return null;
    }
}

/**
 * Ranks race events with the same public criteria exposed by category pages.
 * Events lacking a criterion's required data are omitted; stable sort keeps
 * the map/config order for ties. Volume retains the section's original policy
 * of excluding races that have never traded.
 */
export function rankEventsBySort(events: readonly RaceEvent[], sort: SearchSeriesSortCriteria): EventSortScore[] {
    return events
        .map((event) => ({ ticker: event.ticker, score: eventSortScore(event, sort) }))
        .filter(
            (entry): entry is EventSortScore =>
                entry.score !== null && (sort !== SearchSeriesSortCriteria.VOLUME || entry.score > 0),
        )
        .sort((a, b) => b.score - a.score);
}

function marketPartySuffix(tickerName: string): string {
    return tickerName.slice(tickerName.lastIndexOf('-') + 1).toUpperCase();
}

/** The party a market ticker's suffix implies; null for candidate/independent markets. */
export function partyFromMarketTicker(tickerName: string): MajorRaceParty | null {
    const suffix = marketPartySuffix(tickerName);
    if (DEM_MARKET_SUFFIXES.has(suffix)) return 'dem';
    if (REP_MARKET_SUFFIXES.has(suffix)) return 'rep';
    return null;
}

/** Political-party initial from a hydrated structured target. */
export function partyFromInitial(initial: unknown): RaceMarketParty | null {
    if (typeof initial !== 'string') return null;
    switch (initial.toUpperCase()) {
        case 'D':
            return 'dem';
        case 'R':
            return 'rep';
        case 'I':
            return 'ind';
        default:
            return null;
    }
}

/**
 * A race market's party by the hub's rules: its political-party target's
 * initial, else the ticker suffix, else Independent for the custom candidate
 * markets that carry no party target (Nebraska's `SENATENE-26-DOSB`). Null for
 * a market whose party target names no known party.
 */
export function raceMarketParty(ticker: string, partyTargetId: unknown, initial: unknown): RaceMarketParty | null {
    const structuredParty = partyFromInitial(initial);
    if (structuredParty !== null) return structuredParty;
    const majorParty = partyFromMarketTicker(ticker);
    if (majorParty !== null) return majorParty;
    if (IND_MARKET_SUFFIXES.has(marketPartySuffix(ticker))) return 'ind';
    return partyTargetId === undefined ? 'ind' : null;
}

/**
 * A race's stable display pair: the first Democrat left and the first
 * Republican right, with the best Independent replacing the lower-priced
 * major-party side only when its price is strictly higher. Null without both
 * major parties (primaries, single-party races).
 */
export function selectRaceMatchup<T>(
    items: readonly T[],
    partyOf: (item: T) => RaceMarketParty,
    priceOf: (item: T) => number,
): [T, T] | null {
    let dem: T | undefined;
    let rep: T | undefined;
    let independent: T | undefined;
    for (const item of items) {
        const party = partyOf(item);
        if (party === 'dem') dem ??= item;
        if (party === 'rep') rep ??= item;
        if (party === 'ind' && (independent === undefined || priceOf(item) > priceOf(independent))) {
            independent = item;
        }
    }
    if (dem === undefined || rep === undefined) return null;

    const pair: [T, T] = [dem, rep];
    if (independent === undefined) return pair;
    const lowerMajorIndex = priceOf(dem) <= priceOf(rep) ? 0 : 1;
    if (priceOf(independent) > priceOf(pair[lowerMajorIndex])) pair[lowerMajorIndex] = independent;
    return pair;
}

/** A market's authoritative hydrated party, then its ticker-suffix fallback. */
export function marketParty(market: RaceMarket): RaceMarketParty | null {
    if (market.party !== undefined) return market.party;
    const majorParty = partyFromMarketTicker(market.ticker_name);
    if (majorParty !== null) return majorParty;
    return IND_MARKET_SUFFIXES.has(marketPartySuffix(market.ticker_name)) ? 'ind' : null;
}

/**
 * Attaches structured-target party identities to REST/history event markets.
 * Unresolved markets are left untouched so `marketParty` falls back to their
 * ticker suffix. References are preserved when no market needs enrichment.
 */
export function withRaceMarketParties(
    events: readonly RaceEvent[],
    partyByMarketTicker: RaceMarketParties,
): readonly RaceEvent[] {
    let eventsChanged = false;
    const enriched = events.map((event) => {
        let marketsChanged = false;
        const markets = event.markets?.map((market) => {
            const party = partyByMarketTicker[market.ticker_name];
            if (party === undefined || party === market.party) return market;
            marketsChanged = true;
            return { ...market, party };
        });
        if (!marketsChanged) return event;
        eventsChanged = true;
        return { ...event, markets };
    });
    return eventsChanged ? enriched : events;
}

/**
 * A race whose every candidate market resolves to the same major party is a
 * guaranteed seat for that party regardless of which candidate wins. At
 * least two fully resolved markets are required; unknown/Independent markets
 * make the race non-unanimous rather than guessing.
 */
export function sameMajorPartyFromMarkets(markets: readonly RaceMarket[]): MajorRaceParty | null {
    if (markets.length < 2) return null;
    let sameParty: MajorRaceParty | null = null;
    for (const market of markets) {
        const party = marketParty(market);
        if (party !== 'dem' && party !== 'rep') return null;
        if (sameParty !== null && party !== sameParty) return null;
        sameParty = party;
    }
    return sameParty;
}

/** The shared pricing policy (see ./yesProbability.ts) over a race market. */
export function marketYesProbability(market: RaceMarket): number | null {
    return yesProbabilityFromCents({
        result: market.result,
        bid: market.yes_bid,
        ask: market.yes_ask,
        last: market.last_price,
    });
}

/** Each party's own market within a race event, using hydrated identity then suffix fallback. */
export function partyMarkets(markets: readonly RaceMarket[]): { dem: RaceMarket | null; rep: RaceMarket | null } {
    return {
        dem: markets.find((market) => marketParty(market) === 'dem') ?? null,
        rep: markets.find((market) => marketParty(market) === 'rep') ?? null,
    };
}

/**
 * Each party's win probability from its OWN market: a settled result, else
 * the last trade price, else the mid of a two-sided never-traded book.
 * Null when that party has no usable market. Kept separate (not `1 - other`)
 * because races with independent candidates don't sum D + R to 1.
 */
export function partyWinProbabilities(markets: readonly RaceMarket[]): { dem: number | null; rep: number | null } {
    const { dem, rep } = partyMarkets(markets);
    return {
        dem: dem ? marketYesProbability(dem) : null,
        rep: rep ? marketYesProbability(rep) : null,
    };
}

/**
 * Converts independently priced party probabilities into the Democratic-keyed
 * probability consumed by the rating buckets. The higher-priced party wins:
 * keep the Democratic probability when it is at least the Republican one,
 * otherwise invert the Republican probability. With only one priced party,
 * use that party directly (inverting Republicans); null means neither is
 * usable.
 *
 * This makes a displayed 97% Republican favorite bucket as 3% Democratic,
 * even when the independently traded Democratic market last traded above 3%.
 */
export function demWinProbabilityFromPartyProbabilities({
    dem,
    rep,
}: {
    dem: number | null;
    rep: number | null;
}): number | null {
    if (dem === null) return rep === null ? null : new Decimal(1).minus(rep).toNumber();
    if (rep === null || dem >= rep) return dem;
    return new Decimal(1).minus(rep).toNumber();
}

/** Democratic-keyed rating probability derived from a race's party markets. */
export function demWinProbabilityFromMarkets(markets: readonly RaceMarket[]): number | null {
    const sameParty = sameMajorPartyFromMarkets(markets);
    if (sameParty !== null) return sameParty === 'dem' ? 1 : 0;
    return demWinProbabilityFromPartyProbabilities(partyWinProbabilities(markets));
}
